/**
 * TextEditorWidget — T3.12 catalog entry for contentType 'text'.
 *
 * Replaces the contenteditable placeholder (TextWidget) with the
 * full @fredericrous/lexical-multi editor surface: 6 heading levels,
 * lists, links, code blocks, tables, emoji, embeds, etc.
 *
 * State storage: `contentProps.lexicalState` holds the serialized
 * LexicalEditorState as a JSON string. On every onChange, we write
 * it back via useUpdateGrid().updateItem() — same pattern as the
 * contenteditable TextWidget, just richer.
 *
 * Preview mode (passed via contentProps.preview = true): swaps the
 * interactive editor for a read-only div to avoid gesture conflicts
 * with the xyflow canvas at low zoom levels. Preview renders pure HTML
 * and does NOT touch the MultiEditorProvider, so it works standalone.
 */
import { useCallback, useEffect, useMemo, useRef } from "react";
import { html, css } from "react-strict-dom";
import { typography } from "@duro-app/tokens/tokens/typography.css";
import { Editor, useMultiEditor } from "@fredericrous/lexical-multi";
import type { EditorProps } from "@fredericrous/lexical-multi";
import { CLEAR_EDITOR_COMMAND } from "lexical";
import {
  useAutoGrowRef,
  useGridStore,
  useUpdateGrid,
} from "@fredericrous/grid";
import { serializeToHtml } from "@website-builder/lexical-html";
import { usePageEdit } from "../PageEditContext.js";
import type { WidgetProps } from "./types.js";
import { sizes } from "@duro-app/tokens/tokens/sizes.css";

const styles = css.create({
  // The grid item wrapper sets user-select:none + touch-action:none for drag UX
  // (GridItem.tsx); those cascade in and kill the caret + selection. Re-enable
  // text interaction. cursor/touch-action are web concerns (this widget is
  // web-only — native uses NativeTextWidget).
  editor: {
    width: "100%",
    minHeight: sizes.placeholderMinH,
    fontSize: typography.fontSizeSm,
    userSelect: "text",
    touchAction: "auto",
    cursor: "text",
  },
  // Select→drill-in (grid 0.8.40): the OUTER wrapper owns the drill-in gesture
  // and stays pointer-interactive; the INNER gate below toggles whether the
  // Lexical surface itself receives pointer events. height:100% so the gesture
  // covers the WHOLE tile — with content-height only, a click in the tile's
  // empty lower area still selects (the grid item handles that) but never
  // reaches these handlers, so drill-in "randomly" does nothing. The auto-grow
  // ref therefore sits on the inner gate (content height), not on this
  // stretched wrapper — measuring the wrapper would report the tile's own
  // height back to auto-grow and pin it there.
  wrap: {
    width: "100%",
    height: "100%",
    minHeight: sizes.placeholderMinH,
    fontSize: typography.fontSizeSm,
  },
  // NOT drilled in: the caret must not be stealable — pointer-events:none lets a
  // click fall through to the outer wrapper's drill-in handlers (and up to the
  // grid item for select/drag) instead of landing a caret. Cursor stays the
  // grid's (grab/default), not text.
  gateInert: {
    pointerEvents: "none",
    userSelect: "none",
    cursor: "default",
  },
  // Drilled in: full text interaction, but NO frame of its own. The editable is
  // only as tall as its text; a border around just that region contradicts the
  // "click anywhere in the tile edits it" model (the whole tile is the input).
  // Edit state is already carried WYSIWYG-style by the grid's selection ring
  // around the whole tile + the live caret + the Format toolbar — so the tile
  // reads as one seamless input, matching the published page.
  gateLive: {
    pointerEvents: "auto",
    userSelect: "text",
    touchAction: "auto",
    cursor: "text",
  },
});

/**
 * Coerce a stored `lexicalState` into the shape the <Editor> expects.
 *
 * The <Editor> wants a SerializedEditorState *object* — it gates
 * hydration on `editorState.root.children.length` and stringifies it
 * itself. We persist lexicalState as a JSON *string* (the format the
 * publish renderer + serializeSiteDoc expect), so parse it back to an
 * object here. Without this, reload-restored text never appears: the
 * Editor sees a string, `string.root` is undefined, and it mounts
 * empty. Tolerate an already-parsed object too; garbage → null.
 */
export function toInitialEditorState(
  lexicalState: string | object | null | undefined,
): EditorProps["editorState"] {
  if (!lexicalState) return null;
  if (typeof lexicalState === "object")
    return lexicalState as EditorProps["editorState"];
  try {
    return JSON.parse(lexicalState) as EditorProps["editorState"];
  } catch {
    return null;
  }
}

/**
 * SEMANTIC emptiness for the first-click-goes-to-typing fast path (D2).
 * A `lexicalStr.length === 0` check is not enough: Lexical fires an initial
 * onChange on mount, so a never-typed-in widget still persists a root with one
 * empty paragraph — and every existing draft already carries such states. Walk
 * the serialized tree for any actual text instead.
 */
export function isLexicalStateEmpty(
  lexicalStr: string | null | undefined,
): boolean {
  if (!lexicalStr) return true;
  try {
    const hasText = (node: unknown): boolean => {
      if (node == null || typeof node !== "object") return false;
      const n = node as { text?: unknown; children?: unknown };
      if (typeof n.text === "string" && n.text.length > 0) return true;
      return Array.isArray(n.children) && n.children.some(hasText);
    };
    return !hasText((JSON.parse(lexicalStr) as { root?: unknown }).root);
  } catch {
    return true;
  }
}

// Cell growth to fit the editor's content is the GRID's `useAutoGrowRef`
// (0.9.4) — a callback ref owning the ResizeObserver → report wiring that was
// hand-rolled here. It observes CONTENT-sized elements (the drill-in gate /
// preview div — never the stretched wrapper, which would echo the tile height
// back and pin it), so contentRect.height is the natural text height the old
// scrollHeight read reported.

/**
 * Interactive editor branch. Split out from the preview branch so the
 * `useMultiEditor` hook — which requires a MultiEditorProvider — is only
 * called where one is guaranteed (inside the editor screen), never in the
 * standalone preview/publish contexts.
 */
function InteractiveTextEditor({ itemId, contentProps, gridId }: WidgetProps) {
  const lexicalState = contentProps?.["lexicalState"] as
    | string
    | object
    | null
    | undefined;
  const { updateItem } = useUpdateGrid(gridId);
  const editorRef = useAutoGrowRef(gridId, itemId);

  const initialEditorState = useMemo(
    () => toInitialEditorState(lexicalState),
    [lexicalState],
  );

  // Lexical reads `editorState` ONLY at mount, so an external change to
  // lexicalState (undo/redo, or a remote peer) wouldn't update the visible text.
  // Track the last state WE persisted; when the incoming state differs, it's
  // external. Our own edits keep the ref in sync, so typing never re-applies.
  const lexicalStr =
    typeof lexicalState === "string"
      ? lexicalState
      : lexicalState
        ? JSON.stringify(lexicalState)
        : "";
  const lastWrittenRef = useRef(lexicalStr);
  const applyingExternalRef = useRef(false);

  // The live Lexical editor instance for THIS widget, registered by `<Editor
  // id>` into the MultiEditorProvider map (MultiEditorStorePlugin). Grabbing it
  // lets us apply an external state change IN PLACE via setEditorState — the
  // editor instance, its DOM, focus and caret all survive. (The prior fix
  // remounted the editor via a React key on every undo, which worked but tore
  // down and rebuilt the whole editor + all its plugins each time.)
  const editor = useMultiEditor(itemId);
  useEffect(() => {
    if (!editor) return;
    if (lexicalStr === lastWrittenRef.current) return; // our own edit — skip
    lastWrittenRef.current = lexicalStr;
    applyingExternalRef.current = true;
    try {
      if (lexicalStr) {
        editor.setEditorState(editor.parseEditorState(lexicalStr));
      } else {
        // Undo back to the freshly-created (empty) item — blank the doc.
        editor.dispatchCommand(CLEAR_EDITOR_COMMAND, undefined);
      }
    } catch {
      // Malformed state — leave the editor as-is rather than crash.
    } finally {
      // setEditorState / dispatchCommand commit synchronously and fire the
      // update listener (OnChangePlugin) before returning, so the guard above
      // is still held when the resulting onChange runs.
      applyingExternalRef.current = false;
    }
  }, [editor, lexicalStr]);

  const handleChange: NonNullable<EditorProps["onChange"]> = useCallback(
    (editorState) => {
      // Ignore the onChange our own setEditorState/clear triggers — re-persisting
      // it is a redundant write and would re-enter the undo transaction.
      if (applyingExternalRef.current) return;
      const json = JSON.stringify(editorState);
      lastWrittenRef.current = json; // our own edit — don't treat as external
      updateItem(itemId, {
        contentProps: { ...(contentProps ?? {}), lexicalState: json },
      });
    },
    [itemId, contentProps, updateItem],
  );

  // ── Select→drill-in (grid 0.8.40) ──────────────────────────────────────────
  // Under `activation: "select-first"` a first click SELECTS the tile (grid),
  // and only a drill-in gesture reaches the caret: a click on an ALREADY-selected
  // tile (Figma-style). This holds whether or not the widget has text — an empty
  // tile also takes two clicks (select, then edit), so you can select it to move
  // it without falling into the caret. Esc steps back out to selected. (RSD
  // html.* has no onDoubleClick, so the two-click path IS the drill-in.) In
  // legacy "press" mode we keep the old always-editable behaviour untouched.
  // Keyboard users still get type-to-edit (below) — Tab to a tile, start typing.
  const { editingItemId, setEditingItemId } = usePageEdit();
  const editing = editingItemId === itemId;
  const selectFirst =
    useGridStore((s) => s.grids[gridId]?.config.activation) === "select-first";
  const selected = useGridStore(
    (s) =>
      s.selection?.gridId === gridId && s.selection.itemIds.includes(itemId),
  );

  // TYPE-TO-EDIT (a11y, below): the printable key that opened edit mode is
  // parked here and inserted once the editor is focused — the char can't type
  // itself because the editor isn't mounted-focused at the triggering keydown.
  const pendingCharRef = useRef<string | null>(null);

  // Place the caret the moment we drill in; on the way OUT, hand focus back to
  // the grid box so keyboard nav continues.
  const wasEditingRef = useRef(false);
  // Set at the Escape keydown so the exit effect knows this exit was a keyboard
  // step-out (return focus to the box) vs a click onto another tile (leave
  // focus where the click put it).
  const escapeExitRef = useRef(false);
  useEffect(() => {
    if (!editor) return;
    const wasEditing = wasEditingRef.current;
    wasEditingRef.current = editing;
    if (editing) {
      const pending = pendingCharRef.current;
      pendingCharRef.current = null;
      editor.focus(
        pending
          ? // Type-to-edit: park the caret at the doc end so the inserted char
            // appends rather than landing at a stale position. execCommand is
            // the version-agnostic insert (fires the beforeinput Lexical's own
            // plugin handles) — using a Lexical node helper here would import a
            // DIFFERENT lexical instance than the editor (dual 0.41/0.45 bundles)
            // and throw "no active editor state". Feature-detected: absent in
            // jsdom, deprecated but universally present in real browsers.
            () => {
              if (typeof document.execCommand === "function")
                document.execCommand("insertText", false, pending);
            }
          : undefined,
        { defaultSelection: "rootEnd" },
      );
      return;
    }
    // Left edit (true→false) via ESCAPE: return focus to the grid box so a
    // keyboard user keeps their place for tab/arrow nav. Deferred one frame —
    // Lexical blurs the editable to <body> during teardown, and an immediate
    // box.focus() would be undone by that blur.
    //
    // ONLY on an Escape exit (escapeExitRef), NEVER when edit ended because the
    // user CLICKED ANOTHER tile: focus has already moved to that tile, and
    // pulling it back to this box would send the next keystrokes (via
    // type-to-edit) to the tile they just left (user report 2026-07-21: "type
    // in tile B, it writes in A"). A flag set at the Escape keydown is
    // deterministic — unlike sniffing activeElement, which races Lexical's blur.
    if (wasEditing && escapeExitRef.current) {
      escapeExitRef.current = false;
      const box = editor
        .getRootElement?.()
        ?.closest<HTMLElement>("[data-grid-item]");
      if (box) requestAnimationFrame(() => box.focus());
    }
  }, [editing, editor]);

  // Clicking away (another widget drills in, or the canvas clears the grid
  // selection) must also leave edit mode — editing always implies selected, so
  // losing the selection is the exit signal.
  useEffect(() => {
    if (editing && !selected) setEditingItemId(null);
  }, [editing, selected, setEditingItemId]);

  // TYPE-TO-EDIT (keyboard a11y): a keyboard user can Tab to a text tile and
  // move it (Enter grabs, arrows move), but the pointer drill-in is unreachable
  // by keyboard. So when THIS tile's grid box holds keyboard focus and we're not
  // editing, the first printable keystroke enters edit mode and is inserted —
  // the "just start typing" convention. Only single printable characters
  // qualify: Enter stays the grid's grab-to-move, arrows/Escape/shortcuts pass
  // through untouched. The trigger is BOX FOCUS, not store selection — tabbing
  // to a tile focuses it without selecting it (verified), so we select it here
  // too (edit mode implies selection; the exit effect would otherwise kick
  // straight back out). The activeElement check pins it to THIS tile's box.
  useEffect(() => {
    if (!selectFirst || editing) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      if (e.key.length !== 1) return; // printable char only (not Enter/arrows/…)
      const active = document.activeElement;
      if (
        !(active instanceof Element) ||
        active.getAttribute("data-grid-item") !== itemId
      )
        return;
      // Own the key: stop the grid seeing it, and don't let the browser also
      // deliver it (the parked char is re-inserted once the editor focuses).
      e.preventDefault();
      e.stopPropagation();
      pendingCharRef.current = e.key;
      useGridStore.getState().setSelection(gridId, [itemId]);
      setEditingItemId(itemId);
    };
    document.addEventListener("keydown", onKey, true);
    return () => document.removeEventListener("keydown", onKey, true);
  }, [selectFirst, editing, gridId, itemId, setEditingItemId]);

  // Distinguish a click (drill in) from a drag (grid move) by pointer travel.
  // `wasSelected` is captured at POINTERDOWN, BEFORE the grid's select-on-press
  // runs (we're deeper in the bubble path) — the drill-in gate must use it, NOT
  // the render-time `selected` prop: React flushes the pointerdown's select
  // before dispatching pointerup/click, so by then a FIRST click already reads
  // selected=true and would drill straight into edit.
  const handleDown = useCallback(
    (e: {
      clientX?: number;
      clientY?: number;
      target?: EventTarget | null;
      stopPropagation: () => void;
    }) => {
      if (!selectFirst || editing) {
        // Editing (or legacy press mode): own the pointer so the caret lands and
        // the grid never grabs the block mid-edit.
        e.stopPropagation();
        // WHOLE-TILE FOCUS: the editable is only as tall as its text, so a
        // click in the tile's empty lower area lands on this wrapper, not the
        // caret. When editing, forward such a click to the editor (caret to
        // end) so "click anywhere in the block activates the input" holds. A
        // click that DID hit the text falls through untouched — native caret
        // placement at the click point.
        if (editing && editor) {
          const t = e.target;
          const onText =
            t instanceof Element && t.closest("[contenteditable]") != null;
          if (!onText) editor.focus(undefined, { defaultSelection: "rootEnd" });
        }
        return;
      }
      // Not yet editing: DRILL-IN detection. We must NOT use this wrapper's own
      // onPointerUp: the same press bubbles to the GridItem box, which arms its
      // press-or-drag gesture; on release the pointerup RETARGETS to the section
      // block (never back to this wrapper), so an element-level pointerup here
      // never fires and the second click "does nothing" — the long-standing
      // can't-edit-text bug (proven with input instrumentation 2026-07-20). The
      // grid detects its own tap via a WINDOW pointerup listener for exactly the
      // same reason (usePressOrDrag); mirror that. `wasSelected` is read from
      // the store at pointerdown, before the grid's select-on-press flushes.
      const sel = useGridStore.getState().selection;
      const wasSelected =
        !!sel && sel.gridId === gridId && sel.itemIds.includes(itemId);
      const sx = e.clientX ?? 0;
      const sy = e.clientY ?? 0;
      const finish = (up: PointerEvent) => {
        window.removeEventListener("pointerup", finish);
        window.removeEventListener("pointercancel", cancel);
        // Match the grid's own drag-arm threshold (movePx = 8): a press that
        // moved farther armed a grid drag, not a click — don't drill in.
        const moved = Math.hypot(up.clientX - sx, up.clientY - sy) > 8;
        if (moved) return;
        // Click without drag: drill in only when the tile was ALREADY selected
        // before this press. Empty tiles are NOT special-cased — a first click
        // selects (never types), same as a filled tile, so selecting to move an
        // empty block doesn't drop you into the caret (user report 2026-07-22).
        if (wasSelected) setEditingItemId(itemId);
      };
      const cancel = () => {
        window.removeEventListener("pointerup", finish);
        window.removeEventListener("pointercancel", cancel);
      };
      window.addEventListener("pointerup", finish);
      window.addEventListener("pointercancel", cancel);
    },
    [selectFirst, editing, editor, gridId, itemId, setEditingItemId],
  );
  const handleKeyDown = useCallback(
    // RSD types the keydown event minimally (no stopPropagation on the type);
    // the runtime event is a real KeyboardEvent, so cast to reach it. Esc steps
    // out ONE level (edit → selected) — stop it here so the grid doesn't also
    // clear the selection in the same press.
    (e: { key: string }) => {
      if (editing && e.key === "Escape") {
        (e as { stopPropagation?: () => void }).stopPropagation?.();
        escapeExitRef.current = true;
        setEditingItemId(null);
      }
    },
    [editing, setEditingItemId],
  );
  // KEEP FOCUS while editing. The grid item box is `tabindex=0` (keyboard-move
  // a11y), so a mousedown anywhere in the tile's EMPTY area natively moves focus
  // to that box — and that native focus-steal beats editor.focus(), dropping the
  // caret so typing stops (the "click in the tile and the input goes dead" half
  // of the can't-edit bug). preventDefault on MOUSEDOWN (not pointerdown — focus
  // is a mousedown default) blocks the steal; then park the caret at the end so
  // "click anywhere in the tile keeps the input active" holds. A click that DID
  // land on the text is left untouched for native caret placement.
  const handleMouseDown = useCallback(
    (e: { target?: EventTarget | null; preventDefault: () => void }) => {
      if (!editing || !editor) return;
      const t = e.target;
      const onText =
        t instanceof Element && t.closest("[contenteditable]") != null;
      if (!onText) {
        e.preventDefault();
        editor.focus(undefined, { defaultSelection: "rootEnd" });
      }
    },
    [editing, editor],
  );

  // Legacy "press" mode: unchanged — the whole surface is always the live editor
  // and swallows the pointer so a click places the caret.
  if (!selectFirst) {
    return (
      <html.div
        ref={editorRef}
        style={styles.editor}
        onPointerDown={(e: { stopPropagation: () => void }) =>
          e.stopPropagation()
        }
        onMouseDown={(e: { stopPropagation: () => void }) =>
          e.stopPropagation()
        }
      >
        <Editor
          id={itemId}
          editorState={initialEditorState}
          onChange={handleChange}
          placeholder="Add text…"
          // The canvas owns the look: the text must read as the published
          // page, not as a standalone rich-text field.
          appearance="bare"
        />
      </html.div>
    );
  }

  return (
    // Outer wrapper: always pointer-interactive, owns the drill-in gesture and
    // fills the tile so the gesture works ANYWHERE in it. Inner gate: inert
    // until we drill in (so a select-click can't steal a caret), live once
    // editing — it also carries the auto-grow ref (content height; the
    // stretched wrapper would echo the tile height back and pin it).
    <html.div
      style={styles.wrap}
      onPointerDown={handleDown}
      onMouseDown={handleMouseDown}
      onKeyDown={handleKeyDown}
    >
      <html.div
        ref={editorRef}
        style={[editing ? styles.gateLive : styles.gateInert]}
      >
        <Editor
          id={itemId}
          editorState={initialEditorState}
          onChange={handleChange}
          placeholder="Add text…"
          // A11Y: read-only until drilled in — the editable becomes
          // contenteditable="false", which assistive tech announces as a
          // read-only field (the pointer-events gate above is a sighted-only
          // cue). lexical-multi flips this via a plugin INSIDE the editor, so
          // setEditable runs before this widget's focus effect (child effects
          // before parent) and the drill-in caret lands — no focus race.
          editable={editing}
          // The canvas owns the look (see the press-mode editor above).
          appearance="bare"
        />
      </html.div>
    </html.div>
  );
}

/**
 * Read-only preview (the actual site/version preview): render the REAL content
 * as the SAME HTML the publish path emits (serializeToHtml) — so it's
 * WYSIWYG-accurate and inert, without booting the interactive editor.
 */
function TextPreview({ itemId, contentProps, gridId }: WidgetProps) {
  const lexicalState = contentProps?.["lexicalState"] as
    | string
    | object
    | null
    | undefined;
  const editorRef = useAutoGrowRef(gridId, itemId);
  const initialEditorState = useMemo(
    () => toInitialEditorState(lexicalState),
    [lexicalState],
  );
  // initialEditorState is the PARSED object at runtime (toInitialEditorState
  // JSON.parses the string), though its type still admits string — cast to the
  // serializer's param type.
  const html = initialEditorState
    ? serializeToHtml(
        initialEditorState as Parameters<typeof serializeToHtml>[0],
      )
    : "";
  return (
    // Raw on purpose: this renders the publish path's HTML string, and html.*
    // forwards neither dangerouslySetInnerHTML nor className.
    // eslint-disable-next-line duro/no-raw-html-element
    <div
      ref={editorRef}
      className="wb-text-preview"
      style={{
        width: "100%",
        minHeight: sizes.controlLg,
        fontSize: typography.fontSizeSm,
      }}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}

export function TextEditorWidget(props: WidgetProps) {
  const preview = props.contentProps?.["preview"] as boolean | undefined;
  // Conditional COMPONENT choice (not a conditional hook): preview renders pure
  // HTML with no provider dependency; the interactive branch needs the
  // MultiEditorProvider that the editor screen supplies.
  return preview ? (
    <TextPreview {...props} />
  ) : (
    <InteractiveTextEditor {...props} />
  );
}
